import type { ReactNode } from 'react';
import { cardFrame } from '../card/cardGeometry';
import '../card/dossier.css';
import MapNeonDefs from './MapNeonDefs';

// Under the page's base path: the build may be served from /client/, not the site root.
const MARK_URL = `${import.meta.env.BASE_URL}mark-alpha.png`;

export const CARD_WIDTH = 552;
/**
 * Jason, ruled today: the mic and keyboard chips stay on screen during a
 * walk ("a player should be able to talk to Throck when they want"), so this
 * card no longer claims the whole 600px screen. Budget: 600 minus the
 * display's 40px top margin and 24px bottom margin leaves 536 of content
 * area; minus the 88px answer row and the 24px gap above it leaves 424 for
 * the card itself.
 */
export const CARD_HEIGHT = 424;
export const CARD_MARGIN = 24;

export interface MapCardProps {
  /** The grey status word: the place name; on arrival the hero word says ARRIVED. */
  status: string;
  /** The map body: an SVG viewBox sized to the card's content box. */
  children: ReactNode;
}

/**
 * The map screens' card, mirroring the main glasses screen's dossier card
 * (`app/src/glasses/card/DossierCard.tsx`, `dossier.css`, `cardGeometry.ts`
 * — imported, not edited, per Jason's "bolder, use more space" direction on
 * 2026-09-28): the same chamfered panel, lit left edge in two-stage neon,
 * unlit right edges, and o¿o-mark header. The dossier card measures its own
 * height from typed text; the map's content is a fixed 600x600 SVG scaled
 * into the card instead, so this card is a fixed 552x552 square rather than
 * text-measured.
 */
export default function MapCard({ status, children }: MapCardProps) {
  const frame = cardFrame(CARD_WIDTH, CARD_HEIGHT);
  return (
    <section
      className="dossier-card mu-map-card"
      style={{ width: CARD_WIDTH, height: CARD_HEIGHT }}
      aria-label={status}
    >
      <svg
        className="dossier-frame"
        width={CARD_WIDTH}
        height={CARD_HEIGHT}
        viewBox={`0 0 ${CARD_WIDTH} ${CARD_HEIGHT}`}
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="mu-card-bleed" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#1E404A" />
            <stop offset="0.12" stopColor="#172C36" />
            <stop offset="0.5" stopColor="#13212B" />
            <stop offset="1" stopColor="#111F28" />
          </linearGradient>
        </defs>
        <MapNeonDefs />
        <path d={frame.outline} fill="url(#mu-card-bleed)" />
        <path d={frame.hairline} fill="none" stroke="#4E8088" strokeWidth={2.5} />
        <g fill="none" stroke="#4A7A82" strokeWidth={8}>
          {frame.unlit.map((d) => <path key={d} d={d} />)}
        </g>
        <g fill="none" stroke="#B2FAFF" strokeWidth={16} strokeLinejoin="miter" filter="url(#mu-neon)">
          {frame.lit.map((d) => <path key={d} d={d} />)}
        </g>
      </svg>
      <div className="dossier-content mu-map-card-content">
        <header className="dossier-header">
          <img className="dossier-mark" src={MARK_URL} alt="" />
          <span className="dossier-dot" />
          <span className="dossier-status">{status}</span>
        </header>
        <div className="dossier-rule">
          <span className="dossier-rule-lit" />
        </div>
        <div className="mu-map-card-body">{children}</div>
      </div>
    </section>
  );
}
